/**
 * Validation Constants
 *
 * Shared validation rules for entry data across web and mobile.
 */

export const VALIDATION = {
  content: {
    minLength: 1,
    maxLength: 500,
  },
  satisfaction: {
    min: 1,
    max: 5,
  },
  timeSpentMinutes: {
    min: 0,
    max: 1440, // 24 hours
  },
} as const;

/**
 * Validate entry content
 */
export function validateContent(content: string): { valid: boolean; error?: string } {
  const trimmed = content.trim();
  if (trimmed.length < VALIDATION.content.minLength) {
    return { valid: false, error: 'Please describe what you accomplished' };
  }
  if (trimmed.length > VALIDATION.content.maxLength) {
    return { valid: false, error: `Content must be ${VALIDATION.content.maxLength} characters or less` };
  }
  return { valid: true };
}

/**
 * Validate satisfaction rating
 */
export function validateSatisfaction(satisfaction: number | null | undefined): { valid: boolean; error?: string } {
  if (satisfaction === null || satisfaction === undefined) {
    return { valid: true }; // Optional field
  }
  if (!Number.isInteger(satisfaction)) {
    return { valid: false, error: 'Satisfaction must be a whole number' };
  }
  if (satisfaction < VALIDATION.satisfaction.min || satisfaction > VALIDATION.satisfaction.max) {
    return { valid: false, error: `Satisfaction must be between ${VALIDATION.satisfaction.min} and ${VALIDATION.satisfaction.max}` };
  }
  return { valid: true };
}

/**
 * Validate time spent in minutes
 */
export function validateTimeSpent(minutes: number | null | undefined): { valid: boolean; error?: string } {
  if (minutes === null || minutes === undefined) {
    return { valid: true }; // Optional field
  }
  if (!Number.isInteger(minutes)) {
    return { valid: false, error: 'Time spent must be a whole number' };
  }
  if (minutes < VALIDATION.timeSpentMinutes.min) {
    return { valid: false, error: 'Time spent cannot be negative' };
  }
  if (minutes > VALIDATION.timeSpentMinutes.max) {
    return { valid: false, error: `Time spent cannot exceed ${VALIDATION.timeSpentMinutes.max} minutes (24 hours)` };
  }
  return { valid: true };
}

/**
 * Validate all entry fields at once
 */
export function validateEntry(data: {
  content: string;
  satisfaction?: number | null;
  timeSpentMinutes?: number | null;
}): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  const contentResult = validateContent(data.content);
  if (!contentResult.valid && contentResult.error) {
    errors.push(contentResult.error);
  }

  const satisfactionResult = validateSatisfaction(data.satisfaction);
  if (!satisfactionResult.valid && satisfactionResult.error) {
    errors.push(satisfactionResult.error);
  }

  const timeResult = validateTimeSpent(data.timeSpentMinutes);
  if (!timeResult.valid && timeResult.error) {
    errors.push(timeResult.error);
  }

  return { valid: errors.length === 0, errors };
}
